import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Accessibility as AccessibilityIcon, Eye, Ear, Hand, Monitor } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Accessibility = () => {
  const [customContent, setCustomContent] = useState(null);
  const navigate = useNavigate();
  
  // Hidden admin access - click icon 5 times quickly
  const clickCount = useRef(0);
  const clickTimer = useRef(null);
  
  const handleIconClick = () => {
    clickCount.current += 1;
    
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
    }
    
    if (clickCount.current >= 5) {
      clickCount.current = 0;
      navigate('/admin');
    } else {
      clickTimer.current = setTimeout(() => {
        clickCount.current = 0;
      }, 2000); // Reset after 2 seconds of no clicks
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/settings`);
        if (res.data.accessibility_content) {
          setCustomContent(res.data.accessibility_content);
        }
      } catch (e) {
        console.error('Failed to fetch settings:', e);
      }
    };
    fetchSettings();
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      
      {/* Hero Section */}
      <div className="pt-44 pb-12 bg-gradient-to-br from-[#003456] to-[#002B47]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div 
            onClick={handleIconClick}
            className="w-20 h-20 bg-[#CE8946] rounded-full flex items-center justify-center mx-auto mb-6 cursor-default select-none"
            title="Accessibility"
          >
            <AccessibilityIcon className="h-10 w-10 text-[#003456]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Accessibility Statement</h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            LyphLine Freeze Dryers is committed to ensuring digital accessibility for people with disabilities.
          </p>
        </div>
      </div>
      
      <div className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          {customContent ? (
            <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 prose prose-lg max-w-none">
              <div className="text-gray-600" dangerouslySetInnerHTML={{ __html: customContent }} />
            </div>
          ) : (
            <>
              {/* Accessibility Features */}
              <div className="grid md:grid-cols-3 gap-6 mb-12">
                {[
                  { icon: Eye, title: 'Visual', description: 'Screen reader compatible' },
                  { icon: Ear, title: 'Auditory', description: 'Text alternatives' },
                  { icon: Hand, title: 'Motor', description: 'Keyboard navigation' }
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-2xl p-6 text-center shadow-sm">
                    <item.icon className="h-8 w-8 text-[#003456] mx-auto mb-3" />
                    <p className="font-semibold text-[#0a0203]">{item.title}</p>
                    <p className="text-gray-500 text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
              
              <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 prose prose-lg max-w-none">
                <h2 className="text-2xl font-semibold text-[#0a0203] mb-4">Our Commitment</h2>
                <p className="text-gray-600 mb-6">
                  LyphLine Freeze Dryers is committed to ensuring that our website is accessible to people with disabilities. 
                  We continually improve the user experience for everyone, and apply the relevant accessibility standards.
                </p>

                <h2 className="text-2xl font-semibold text-[#0a0203] mt-8 mb-4">Conformance Status</h2>
                <p className="text-gray-600 mb-6">
                  The Web Content Accessibility Guidelines (WCAG) defines requirements for designers and developers to 
                  improve accessibility for people with disabilities. It defines three levels of conformance: Level A, 
                  Level AA, and Level AAA. LyphLine Freeze Dryers strives to conform to WCAG 2.1 level AA.
                </p>

                <h2 className="text-2xl font-semibold text-[#0a0203] mt-8 mb-4">Accessibility Features</h2>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Keyboard Navigation:</strong> All interactive elements are accessible via keyboard</li>
                  <li><strong>Screen Reader Support:</strong> Our site is compatible with screen readers</li>
                  <li><strong>Alt Text:</strong> Images include alternative text descriptions</li>
                  <li><strong>Color Contrast:</strong> Text meets WCAG contrast requirements</li>
                  <li><strong>Resizable Text:</strong> Text can be resized up to 200% without loss of content</li>
                  <li><strong>Focus Indicators:</strong> Clear visual focus indicators for navigation</li>
                </ul>

                <h2 className="text-2xl font-semibold text-[#0a0203] mt-8 mb-4">Product Accessibility</h2>
                <p className="text-gray-600 mb-6">
                  LyphLine Freeze Dryers freeze dryers are designed with accessibility in mind:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Clear, high-contrast digital displays</li>
                  <li>Audible alerts and notifications</li>
                  <li>Simple, intuitive controls</li>
                  <li>Detailed printed and digital manuals available in multiple formats</li>
                </ul>

                <h2 className="text-2xl font-semibold text-[#0a0203] mt-8 mb-4">Feedback</h2>
                <p className="text-gray-600 mb-4">
                  We welcome your feedback on the accessibility of the LyphLine Freeze Dryers website. Please let us know if you 
                  encounter accessibility barriers:
                </p>
                <p className="text-gray-600">
                  <strong>Email:</strong> <a href="mailto:support@homelyph.com" className="text-[#003456] hover:underline">support@homelyph.com</a><br />
                  <strong>Phone:</strong> 1-888-223-1435
                </p>

                <h2 className="text-2xl font-semibold text-[#0a0203] mt-8 mb-4">Compatibility</h2>
                <p className="text-gray-600 mb-6">
                  LyphLine Freeze Dryers is designed to be compatible with the following assistive technologies:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>JAWS (Windows)</li>
                  <li>NVDA (Windows)</li>
                  <li>VoiceOver (macOS and iOS)</li>
                  <li>TalkBack (Android)</li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Accessibility;
